import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import {
  faSpinner,
  faInfoCircle,
  faExclamationTriangle,
  faTimesCircle,
  faBug,
  faSkull,
  faFilter,
  faRefresh,
  faArrowLeft,
  faPause,
  faPlay
} from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import type { JobLog, JobLogID, JobExecution, JobType, JobLogLevel } from '@/models';

const AUTO_REFRESH_INTERVAL = 2000;

const pageSize = 50;

// while an execution is running the logs are tailed instead of paginated - TAIL_FETCH_LIMIT logs are
// fetched per refresh and at most TAIL_MAX_ENTRIES are kept in the view
const TAIL_FETCH_LIMIT = 250;
const TAIL_MAX_ENTRIES = 500;

// the log-counters of the execution are updated live, so we can show the correct total without an extra query
const executionLogCount = (execution: JobExecution, level: JobLogLevel | ''): number => {
  switch (level) {
    case 'DEBUG':
      return execution.logCountDebug;
    case 'INFO':
      return execution.logCountInfo;
    case 'WARN':
      return execution.logCountWarn;
    case 'ERROR':
      return execution.logCountError;
    case 'FATAL':
      return execution.logCountFatal;
    default:
      return execution.logCount;
  }
};

export default function AdminJobLogs() {
  const { jobType, executionId } = useParams<{ jobType: JobType; executionId: string }>();
  const [logs, setLogs] = useState<JobLog[]>([]);
  const [execution, setExecution] = useState<JobExecution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [levelFilter, setLevelFilter] = useState<JobLogLevel | ''>('');
  const [tailActive, setTailActive] = useState(false); // the view shows the newest logs instead of a single page
  const [livePaused, setLivePaused] = useState(false);
  const refreshIntervalRef = useRef<number | null>(null);
  const lastLogIdRef = useRef<JobLogID | null>(null);
  const tailGenerationRef = useRef(0); // incremented whenever the tail is (re-)started, so that results of outdated requests are dropped
  const pollInFlightRef = useRef(false);

  const isExecutionRunning = execution?.status === 'RUNNING';
  const isTailing = isExecutionRunning === true && tailActive && !livePaused;
  const isTruncated = tailActive && logs.length < total;

  // Fetch execution status
  const fetchExecution = useCallback(async (): Promise<JobExecution | null> => {
    if (jobType === undefined || executionId === undefined) return null;
    try {
      const response = await APIRoutes.getJobExecution(jobType, executionId);
      setExecution(response);
      return response;
    } catch (err) {
      // Silently ignore errors when fetching execution status during auto-refresh
      console.error('Failed to fetch execution status:', err);
      return null;
    }
  }, [jobType, executionId]);

  // Initial fetch - the execution decides if we tail the logs or show a single page
  useEffect(() => {
    if (jobType === undefined || executionId === undefined) return;

    let cancelled = false;

    const load = async () => {
      const exec = await fetchExecution();
      if (cancelled) return;

      if (exec?.status === 'RUNNING') {
        await startTail(exec);
      } else {
        await fetchLogs();
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [jobType, executionId, levelFilter, currentPage]);

  // Auto-refresh while the execution is running
  useEffect(() => {
    if (!isTailing) return;

    refreshIntervalRef.current = window.setInterval(() => {
      void pollTail();
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      if (refreshIntervalRef.current !== null) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [isTailing, jobType, executionId, levelFilter]);

  // Loads the newest logs of a running execution, all further logs are appended by pollTail()
  const startTail = async (exec: JobExecution) => {
    if (jobType === undefined || executionId === undefined) return;

    tailGenerationRef.current += 1;

    try {
      setLoading(true);
      setError(null);

      const response = await APIRoutes.listJobLogs(jobType, executionId, {
        limit: pageSize,
        sort: '_id',
        sortDirection: 'DESC',
        level: levelFilter === '' ? undefined : levelFilter
      });

      const newest = [...response.logs].reverse();

      lastLogIdRef.current = newest.length > 0 ? newest[newest.length - 1].id : null;

      setLogs(newest);
      setTotal(executionLogCount(exec, levelFilter));
      setTailActive(true);
    } catch (err) {
      setError('Fehler beim Laden der Job-Logs');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  // Appends all logs which were written since the last refresh
  const pollTail = async () => {
    if (jobType === undefined || executionId === undefined) return;
    if (pollInFlightRef.current) return; // a slow refresh must not be polled twice, that would append the same logs again

    const generation = tailGenerationRef.current;
    pollInFlightRef.current = true;

    try {
      // the execution is fetched first - the backend writes the remaining logs before it marks the
      // execution as finished, so a poll after a finished status always contains all remaining logs
      const exec = await fetchExecution();

      const response = await APIRoutes.listJobLogs(jobType, executionId, {
        limit: TAIL_FETCH_LIMIT,
        sort: '_id',
        sortDirection: 'ASC',
        level: levelFilter === '' ? undefined : levelFilter,
        after: lastLogIdRef.current ?? undefined
      });

      if (generation !== tailGenerationRef.current) return; // the tail was restarted in the meantime

      if (response.logs.length > 0) {
        lastLogIdRef.current = response.logs[response.logs.length - 1].id;

        setLogs(prev => {
          const merged = [...prev, ...response.logs];
          return merged.length > TAIL_MAX_ENTRIES ? merged.slice(merged.length - TAIL_MAX_ENTRIES) : merged;
        });
      }

      if (exec !== null) {
        const logCount = executionLogCount(exec, levelFilter);
        setTotal(logCount);

        // the execution finished - switch to the paginated view of the last page, which is where the tail ended
        if (exec.status !== 'RUNNING') {
          setTailActive(false);
          setCurrentPage(Math.max(1, Math.ceil(logCount / pageSize)));
        }
      }
    } catch (err) {
      // Silently ignore errors during auto-refresh
      console.error('Failed to fetch job logs:', err);
    } finally {
      pollInFlightRef.current = false;
    }
  };

  const fetchLogs = async () => {
    if (jobType === undefined || executionId === undefined) return;

    try {
      setLoading(true);
      setError(null);

      // Generate cursor token for page-based navigation
      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listJobLogs(jobType, executionId, {
        limit: pageSize,
        next: cursorToken,
        sort: '_id',
        sortDirection: 'ASC',
        level: levelFilter === '' ? undefined : levelFilter
      });

      lastLogIdRef.current = null;

      setLogs(response.logs);
      setTotal(response.total);
      setTailActive(false);
    } catch (err) {
      setError('Fehler beim Laden der Job-Logs');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const reload = async () => {
    if (tailActive && execution !== null) {
      await startTail(execution);
    } else {
      await fetchLogs();
    }
  };

  const toggleLive = async () => {
    if (livePaused) {
      setLivePaused(false);
      if (execution !== null) {
        await startTail(execution); // jump back to the newest logs, the ones written while paused can be paged through after the run
      }
    } else {
      setLivePaused(true);
    }
  };

  const getLevelIcon = (level: JobLogLevel) => {
    switch (level) {
      case 'DEBUG':
        return <FontAwesomeIcon icon={faBug} className="w-4 h-4 text-gray-500" />;
      case 'INFO':
        return <FontAwesomeIcon icon={faInfoCircle} className="w-4 h-4 text-blue-500" />;
      case 'WARN':
        return <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-yellow-500" />;
      case 'ERROR':
        return <FontAwesomeIcon icon={faTimesCircle} className="w-4 h-4 text-red-500" />;
      case 'FATAL':
        return <FontAwesomeIcon icon={faSkull} className="w-4 h-4 text-red-700" />;
      default:
        return null;
    }
  };

  const getLevelBadge = (level: JobLogLevel) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";

    switch (level) {
      case 'DEBUG':
        return `${baseClasses} bg-gray-100 dark:bg-gray-900/50 text-gray-800 dark:text-gray-300`;
      case 'INFO':
        return `${baseClasses} bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300`;
      case 'WARN':
        return `${baseClasses} bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300`;
      case 'ERROR':
        return `${baseClasses} bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300`;
      case 'FATAL':
        return `${baseClasses} bg-red-200 dark:bg-red-800/50 text-red-900 dark:text-red-200`;
      default:
        return `${baseClasses} bg-gray-100 dark:bg-gray-900/50 text-gray-800 dark:text-gray-300`;
    }
  };

  const formatTime = (timeString: string) => {
    const date = new Date(timeString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const renderExtra = (extra: unknown) => {
    if (!extra || typeof extra !== 'object') {
      return null;
    }

    return (
      <pre className="mt-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto">
        {JSON.stringify(extra, null, 2)}
      </pre>
    );
  };

  if (!jobType || !executionId) {
    return (
      <>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400">Ungültige Parameter</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to={`/admin/jobs/${jobType}`}
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Job-Logs
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Logs für {jobType} - Ausführung {executionId.slice(0, 8)}...
                {isExecutionRunning && isTailing && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300">
                    <FontAwesomeIcon icon={faSpinner} className="w-3 h-3 animate-spin mr-1" />
                    Läuft - Live-Ansicht aktiv
                  </span>
                )}
                {isExecutionRunning && !isTailing && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-900/50 text-gray-800 dark:text-gray-300">
                    <FontAwesomeIcon icon={faPause} className="w-3 h-3 mr-1" />
                    Läuft - Live-Ansicht pausiert
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {isExecutionRunning && (
              <button
                onClick={() => void toggleLive()}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors cursor-pointer"
              >
                <FontAwesomeIcon icon={livePaused ? faPlay : faPause} className="w-4 h-4 mr-2" />
                {livePaused ? 'Live fortsetzen' : 'Live pausieren'}
              </button>
            )}
            <button
              onClick={() => void reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
            >
              <FontAwesomeIcon icon={faRefresh} className="w-4 h-4 mr-2" />
              Aktualisieren
            </button>
          </div>
        </div>

        {/* Filter controls */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faFilter} className="w-4 h-4 text-gray-500" />
              <label htmlFor="level-filter" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Log-Level:
              </label>
            </div>
            <select
              id="level-filter"
              value={levelFilter}
              onChange={(e) => {
                setLevelFilter(e.target.value as JobLogLevel | '');
                setCurrentPage(1);
              }}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Alle Level</option>
              <option value="DEBUG">DEBUG</option>
              <option value="INFO">INFO</option>
              <option value="WARN">WARN</option>
              <option value="ERROR">ERROR</option>
              <option value="FATAL">FATAL</option>
            </select>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Log-Einträge ({total})
                {levelFilter !== '' && (
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    (gefiltert nach {levelFilter})
                  </span>
                )}
                {isTruncated && (
                  <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                    (nur die neuesten {logs.length} Einträge werden angezeigt)
                  </span>
                )}
              </h2>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {logs.map((log) => (
                <div key={log.id} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {getLevelIcon(log.level)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={getLevelBadge(log.level)}>
                            {log.level}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {log.type}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTime(log.time)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-900 dark:text-white">
                        <pre className="whitespace-pre-wrap font-mono text-xs break-words">
                          {log.message}
                        </pre>
                      </div>
                      {renderExtra(log.extra)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {logs.length === 0 && (
              <div className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                {levelFilter !== '' ?
                  `Keine Log-Einträge mit Level "${levelFilter}" gefunden.` :
                  'Keine Log-Einträge gefunden.'
                }
              </div>
            )}

            {/* Pagination - while the logs are tailed the newest entries are shown instead of a single page */}
            {!tailActive && (
              <PageBasedPagination
                currentPage={currentPage}
                totalCount={total}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                loading={loading}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
